/* OEO Upload Your Photo collection only. No uploads, cart or external services. */
(() => {
  if (customElements.get('oeo-photo-designs')) return;
  class OeoPhotoDesigns extends HTMLElement {
    async connectedCallback() {
      if (this.controller) return;
      const controller = new AbortController();
      this.controller = controller;
      const signal = controller.signal;
      const filters = this.querySelector('[data-uyp-filters]');
      const status = this.querySelector('[data-uyp-status]');
      try {
        await this.loadAllPages(signal);
        if (signal.aborted || !this.isConnected) return;
        this.cards = [...this.querySelectorAll('[data-uyp-card]')];
        const counts = {
          all: this.cards.length,
          single: this.cards.filter(card => Number(card.dataset.photoCount) === 1).length,
          multi: this.cards.filter(card => Number(card.dataset.photoCount) > 1).length
        };
        for (const [key, value] of Object.entries(counts)) {
          this.querySelector(`[data-uyp-count="${key}"]`).textContent = String(value);
        }
        filters.hidden = false;
        this.addEventListener('click', event => {
          const button = event.target.closest?.('[data-uyp-filter]');
          if (!button || !this.contains(button)) return;
          const selection = button.dataset.uypFilter;
          this.apply(selection);
          const next = new URL(location.href);
          next.searchParams.delete('page');
          if (selection === 'all') next.searchParams.delete('photo_layout');
          else next.searchParams.set('photo_layout', selection);
          if (next.href !== location.href) history.pushState(history.state, '', next);
        }, { signal });
        window.addEventListener('popstate', () => this.apply(this.readSelection()), { signal });
        this.apply(this.readSelection());
        this.dataset.ready = 'true';
      } catch (error) {
        if (signal.aborted) return;
        filters.hidden = true;
        status.textContent = 'Browse the designs below. Filters are temporarily unavailable.';
        this.dataset.ready = 'fallback';
        console.warn('OEO photo design filters: preserving the original collection page.', error.message);
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }
    readSelection() {
      const selection = new URL(location.href).searchParams.get('photo_layout');
      return ['single', 'multi'].includes(selection) ? selection : 'all';
    }
    apply(selection) {
      if (!['all', 'single', 'multi'].includes(selection)) selection = 'all';
      let visible = 0;
      for (const card of this.cards) {
        const count = Number(card.dataset.photoCount);
        const show = selection === 'all' || (selection === 'single' ? count === 1 : count > 1);
        card.hidden = !show;
        if (show) visible += 1;
      }
      for (const button of this.querySelectorAll('[data-uyp-filter]')) {
        button.setAttribute('aria-pressed', String(button.dataset.uypFilter === selection));
      }
      this.querySelector('[data-uyp-status]').textContent = `Showing ${visible} of ${this.cards.length} ${this.cards.length === 1 ? 'design' : 'designs'}.`;
      this.querySelector('[data-uyp-empty]').hidden = visible !== 0;
      const focusedCard = document.activeElement?.closest?.('[data-uyp-card]');
      if (focusedCard && this.contains(focusedCard) && focusedCard.hidden) {
        this.querySelector(`[data-uyp-filter="${selection}"]`).focus({ preventScroll: true });
      }
      this.dataset.selection = selection;
    }
    async loadAllPages(signal) {
      // Collect first, commit once: a failed later page must not produce partial counts.
      let source = this;
      const current = new URL(location.href);
      const request = async rawUrl => {
        const url = new URL(rawUrl, location.href);
        if (url.origin !== current.origin || url.pathname !== current.pathname) throw new Error('Unexpected collection pagination target');
        for (const [key, value] of current.searchParams) {
          if (!['page', 'section_id'].includes(key)) url.searchParams.set(key, value);
        }
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url, { credentials: 'same-origin', signal });
        if (!response.ok) throw new Error(`Collection page HTTP ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const section = [...doc.querySelectorAll('oeo-photo-designs')].find(node => node.dataset.collectionId === this.dataset.collectionId);
        if (!section?.querySelector('[data-uyp-grid]')) throw new Error('Collection identity missing from response');
        return section;
      };
      if (current.searchParams.has('page') && current.searchParams.get('page') !== '1') {
        const first = new URL(current);first.searchParams.delete('page');source = await request(first);
      }
      let next = source.querySelector('[data-uyp-next]');
      if (!next && source === this) return;
      this.querySelector('[data-uyp-status]').textContent = 'Loading all designs…';
      const pool = [...source.querySelectorAll('[data-uyp-card]')].map(card => card.cloneNode(true));
      const seenPages = new Set();
      while (next) {
        const url = new URL(next.getAttribute('href'), location.href);
        const pageNumber = url.searchParams.get('page');
        if (!pageNumber || seenPages.has(pageNumber) || seenPages.size >= 100) throw new Error('Repeated or excessive collection pagination');
        seenPages.add(pageNumber);
        source = await request(url);
        pool.push(...[...source.querySelectorAll('[data-uyp-card]')].map(card => card.cloneNode(true)));
        next = source.querySelector('[data-uyp-next]');
      }
      if (signal.aborted) return;
      const groups = new Map();
      for (const card of pool) {
        const key = card.dataset.designId;
        if (!key) throw new Error('Design identity missing');
        const prior = groups.get(key);
        if (!prior) { groups.set(key, card);continue; }
        if (prior.dataset.photoCount !== card.dataset.photoCount) throw new Error('Inconsistent photo count for design');
        const formats = prior.querySelector('[data-uyp-formats]');
        const productIds = new Set([...formats.querySelectorAll('[data-uyp-product-id]')].map(link => link.dataset.uypProductId));
        for (const link of card.querySelectorAll('[data-uyp-product-id]')) {
          if (!productIds.has(link.dataset.uypProductId)) {
            formats.append(link.cloneNode(true));productIds.add(link.dataset.uypProductId);
          }
        }

      }
      this.querySelector('[data-uyp-grid]').replaceChildren(...groups.values());
      for (const link of this.querySelectorAll('.uyp-next,.uyp-previous')) link.hidden = true;
    }
  }
  customElements.define('oeo-photo-designs', OeoPhotoDesigns);
})();
